import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import styled from 'styled-components';
import { 
  FiHome, 
  FiBarChart2, 
  FiSettings,
  FiLogOut,
  FiCalendar,
  FiUserCheck,
  FiUsers,
  FiVideo,
  FiMessageSquare,
  FiFileText,
  FiHelpCircle,
  FiTarget,
  FiMail,
  FiMenu,
  FiX,
  FiLayout
} from 'react-icons/fi';
import { useAuth } from '../contexts/AuthContext';

const SidebarContainer = styled.aside`
  width: 250px;
  height: 100vh;
  background: ${props => props.theme.colors.dark};
  color: white;
  position: fixed;
  left: 0;
  top: 0;
  overflow-y: auto;
  z-index: 1000;
  transition: transform 0.3s ease;

  @media (max-width: ${props => props.theme.breakpoints.md}) {
    transform: ${props => props.$isOpen ? 'translateX(0)' : 'translateX(-100%)'};
    box-shadow: ${props => props.$isOpen ? '0 0 20px rgba(0,0,0,0.3)' : 'none'};
  }
`;

const MobileOverlay = styled.div`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: ${props => props.$isOpen ? 'block' : 'none'};
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.5);
    z-index: 999;
  }
`;

const MobileMenuButton = styled.button`
  display: none;
  
  @media (max-width: ${props => props.theme.breakpoints.md}) {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 20px;
    left: 20px;
    width: 50px;
    height: 50px;
    border-radius: 50%;
    background: ${props => props.theme.colors.black};
    color: white;
    border: none;
    box-shadow: 0 4px 12px rgba(0, 0, 0, 0.5);
    cursor: pointer;
    z-index: 1001;
    transition: all 0.3s ease;
    
    &:active {
      transform: scale(0.95);
    }
    
    svg {
      font-size: 24px;
    }
  }
`;

const Logo = styled.div`
  padding: ${props => props.theme.spacing.xl};
  border-bottom: 1px solid rgba(255, 255, 255, 0.1);
  
  h2 {
    font-size: ${props => props.theme.fontSizes.xl};
    font-weight: 600;
    color: ${props => props.theme.colors.primary};
  }
  
  p {
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[400]};
    margin-top: ${props => props.theme.spacing.xs};
  }
`;

const Nav = styled.nav`
  padding: ${props => props.theme.spacing.lg} 0;
`;

const NavItem = styled(Link).withConfig({
  shouldForwardProp: (prop) => prop !== '$isActive',
})`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  color: ${props => props.$isActive ? props.theme.colors.primary : props.theme.colors.gray[300]};
  background: ${props => props.$isActive ? 'rgba(52, 152, 219, 0.1)' : 'transparent'};
  border-right: ${props => props.$isActive ? `3px solid ${props.theme.colors.primary}` : '3px solid transparent'};
  transition: all 0.2s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.05);
    color: white;
  }
  
  svg {
    font-size: ${props => props.theme.fontSizes.lg};
  }
  
  span {
    font-weight: ${props => props.$isActive ? '600' : '400'};
  }
`;

const LogoutButton = styled.button`
  display: flex;
  align-items: center;
  gap: ${props => props.theme.spacing.md};
  width: 100%;
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.xl};
  background: none;
  color: ${props => props.theme.colors.gray[300]};
  border: none;
  cursor: pointer;
  transition: all 0.2s ease;
  margin-top: auto;
  
  &:hover {
    background: rgba(231, 76, 60, 0.1);
    color: ${props => props.theme.colors.danger};
  }
  
  svg {
    font-size: ${props => props.theme.fontSizes.lg};
  }
`;

const UserInfo = styled.div`
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xl};
  border-top: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: auto;
  
  .user-name {
    font-weight: 600;
    color: white;
  }
  
  .user-role {
    font-size: ${props => props.theme.fontSizes.sm};
    color: ${props => props.theme.colors.gray[400]};
    text-transform: capitalize;
  }
`;

const Sidebar = () => {
  const location = useLocation();
  const { user, logout, isAdmin } = useAuth();
  const [isOpen, setIsOpen] = useState(false);

  const toggleSidebar = () => setIsOpen(!isOpen);
  const closeSidebar = () => setIsOpen(false);

  const menuItems = [
    { path: '/dashboard', icon: FiHome, label: 'Dashboard' },
    { path: '/build-my-event', icon: FiCalendar, label: 'Build My Event' },
    { path: '/homepage-description', icon: FiFileText, label: 'Homepage Description' },
    { path: '/promoters-management', icon: FiUsers, label: 'Promoters Management' },
    { path: '/chat-management', icon: FiMessageSquare, label: 'Customer Chats' },
    { path: '/chat-questions', icon: FiHelpCircle, label: 'Chat Questions' },
    { path: '/leads', icon: FiTarget, label: 'Leads' },
    { path: '/enquiry', icon: FiMail, label: 'Enquiry' },
    { path: '/human-resource', icon: FiUserCheck, label: 'Human Resource' },
    { path: '/analytics', icon: FiBarChart2, label: 'Analytics' },
  ];

  if (isAdmin) {
    menuItems.push({ path: '/settings', icon: FiSettings, label: 'Settings' });
  }

  return (
    <>
      <MobileMenuButton onClick={toggleSidebar}>
        {isOpen ? <FiX /> : <FiMenu />}
      </MobileMenuButton>
      
      <MobileOverlay $isOpen={isOpen} onClick={closeSidebar} />
      
      <SidebarContainer $isOpen={isOpen}>
        <Logo>
          <h2>Fazyra</h2>
          <p>Admin Portal</p>
        </Logo>
        
        <Nav>
          {menuItems.map((item) => (
            <NavItem
              key={item.path}
              to={item.path}
              $isActive={location.pathname === item.path}
              onClick={closeSidebar}
            >
              <item.icon />
              <span>{item.label}</span>
            </NavItem>
          ))}
        </Nav>
        
        <UserInfo>
          <div className="user-name">{user?.firstName} {user?.lastName}</div>
          <div className="user-role">{user?.role}</div>
        </UserInfo>
        
        <LogoutButton onClick={() => { logout(); closeSidebar(); }}>
          <FiLogOut />
          <span>Logout</span>
        </LogoutButton>
      </SidebarContainer>
    </>
  );
};

export default Sidebar;
